import { useEffect, useState } from 'react';
import { configApi } from '@/api/config';
import { sitesApi } from '@/api/sites';
import { briefs as briefsApi } from '@/api/briefs';
import type { ConfigEntry, Site } from '@/types';
import type { ManagedKeyName } from '@/components/settings/setupInstructions';

export type SetupStepId =
  | 'configure_ai'
  | 'configure_hosting'
  | 'configure_source_control'
  | 'connect_cloudflare_github'
  | 'configure_oauth'
  | 'configure_analytics'
  | 'configure_backups'
  | 'create_first_site'
  | 'first_brainstorm';

export type BackupStatus = 'ok' | 'stale' | 'never' | 'unknown';

export interface SetupStep {
  id: SetupStepId;
  title: string;
  description: string;
  complete: boolean;
  blocked: boolean;
  optional: boolean;
  requiredKeys: ManagedKeyName[];
  href: string;
  nextKey?: ManagedKeyName;
}

export interface SetupStatus {
  steps: SetupStep[];
  entries: ConfigEntry[];
  allRequiredComplete: boolean;
  firstUnconfiguredKey: ManagedKeyName | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface SetupSnapshot {
  entries: ConfigEntry[];
  sites: Site[];
  hasArticle: boolean;
  backupStatus: BackupStatus;
}

interface StepDefinition {
  id: SetupStepId;
  title: string;
  description: string;
  optional: boolean;
  requiredKeys: ManagedKeyName[];
  href: string;
}

const STEP_DEFINITIONS: StepDefinition[] = [
  {
    id: 'configure_ai',
    title: 'Configure AI',
    description:
      'Add your Anthropic API key. Powers every brainstorm, write, review, and illustration call.',
    optional: false,
    requiredKeys: ['anthropic_api_key'],
    href: '/settings',
  },
  {
    id: 'configure_hosting',
    title: 'Configure hosting',
    description:
      'Add your Cloudflare API token and Account ID. Used to provision Pages projects and deploy content.',
    optional: false,
    requiredKeys: ['cloudflare_api_token', 'cloudflare_account_id'],
    href: '/settings',
  },
  {
    id: 'configure_source_control',
    title: 'Configure source control',
    description:
      'Add your GitHub personal access token. The platform pushes new MDX files and SVGs here.',
    optional: false,
    requiredKeys: ['github_token'],
    href: '/settings',
  },
  {
    id: 'connect_cloudflare_github',
    title: 'Connect GitHub to Cloudflare Pages',
    description:
      'One-time OAuth grant in the Cloudflare dashboard. Required before the platform can provision Pages projects programmatically.',
    optional: false,
    requiredKeys: [],
    href: '/settings',
  },
  {
    id: 'configure_oauth',
    title: 'Configure authentication',
    description:
      'Add Google OAuth client ID, secret, and allowed domain so team members can sign in.',
    optional: false,
    requiredKeys: ['google_client_id', 'google_client_secret', 'google_allowed_domain'],
    href: '/settings',
  },
  {
    id: 'configure_analytics',
    title: 'Configure analytics (optional)',
    description:
      'Add your Google Search Console service account JSON. Powers the Analytics page.',
    optional: true,
    requiredKeys: ['gsc_credentials_json'],
    href: '/settings',
  },
  {
    id: 'configure_backups',
    title: 'Configure backup storage (recommended)',
    description:
      'Add an object-store bucket (Cloudflare R2 or AWS S3) so nightly backups of dashboard.db and secrets.db ship off-host. The platform works without it, but you should not run production without it.',
    optional: true,
    requiredKeys: [],
    href: '/settings',
  },
  {
    id: 'create_first_site',
    title: 'Create your first site',
    description: 'Provision a Cloudflare Pages project for your first content site.',
    optional: false,
    requiredKeys: [],
    href: '/sites',
  },
  {
    id: 'first_brainstorm',
    title: 'Generate your first brainstorm',
    description: 'Turn a topic into 3-5 AI-generated article briefs and approve one for the pipeline.',
    optional: false,
    requiredKeys: [],
    // Resolved at derivation time to /sites/<first-slug>/brainstorm; falls
    // back to /sites when no site exists yet.
    href: '/sites',
  },
];

function isKeyConfigured(entries: ConfigEntry[], key: ManagedKeyName): boolean {
  return entries.some((e) => e.key === key && e.configured);
}

async function fetchBackupStatus(): Promise<BackupStatus> {
  try {
    const res = await fetch('/api/health', { credentials: 'include' });
    const body = await res.json();
    const v = String(body.backup ?? 'unknown');
    if (v === 'ok') return 'ok';
    if (v.startsWith('stale')) return 'stale';
    if (v === 'never') return 'never';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

async function fetchSnapshot(): Promise<SetupSnapshot> {
  const [entries, sites, backupStatus] = await Promise.all([
    configApi.list(),
    sitesApi.list(),
    fetchBackupStatus(),
  ]);
  const firstActive = sites.find((s) => s.status === 'active');
  let hasArticle = false;
  if (firstActive) {
    try {
      const approved = await briefsApi.list(firstActive.id, 'approved');
      hasArticle = approved.length > 0;
    } catch {
      hasArticle = false;
    }
  }
  return { entries, sites, hasArticle, backupStatus };
}

let inflight: Promise<SetupSnapshot> | null = null;
let cached: SetupSnapshot | null = null;
const subscribers = new Set<(snap: SetupSnapshot | null, err: string | null) => void>();

function notify(snap: SetupSnapshot | null, err: string | null): void {
  for (const cb of subscribers) cb(snap, err);
}

async function loadSnapshot(force: boolean): Promise<void> {
  if (inflight && !force) {
    await inflight;
    return;
  }
  inflight = fetchSnapshot();
  try {
    const snap = await inflight;
    cached = snap;
    notify(snap, null);
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'failed to load setup status';
    notify(cached, msg);
  } finally {
    inflight = null;
  }
}

function deriveSteps(snap: SetupSnapshot | null): {
  steps: SetupStep[];
  allRequiredComplete: boolean;
  firstUnconfiguredKey: ManagedKeyName | null;
} {
  const entries = snap?.entries ?? [];
  const sites = snap?.sites ?? [];
  const hasArticle = snap?.hasArticle ?? false;
  const backupStatus = snap?.backupStatus ?? 'unknown';

  const completionByStep: Record<SetupStepId, boolean> = {
    configure_ai: snap ? isKeyConfigured(entries, 'anthropic_api_key') : false,
    configure_hosting: snap
      ? isKeyConfigured(entries, 'cloudflare_api_token') &&
        isKeyConfigured(entries, 'cloudflare_account_id')
      : false,
    configure_source_control: snap
      ? isKeyConfigured(entries, 'github_token')
      : false,
    connect_cloudflare_github: sites.some((s) => Boolean(s.cloudflare_project_url)),
    configure_oauth: snap
      ? isKeyConfigured(entries, 'google_client_id') &&
        isKeyConfigured(entries, 'google_client_secret') &&
        isKeyConfigured(entries, 'google_allowed_domain')
      : false,
    configure_analytics: snap ? isKeyConfigured(entries, 'gsc_credentials_json') : false,
    configure_backups: backupStatus === 'ok' || backupStatus === 'stale',
    create_first_site: sites.length > 0,
    first_brainstorm: hasArticle,
  };

  const requiredEntryStepsComplete =
    completionByStep.configure_ai &&
    completionByStep.configure_hosting &&
    completionByStep.configure_source_control;

  // first_brainstorm deep-links into a real site's brainstorm page once one
  // exists. Before any site is created the link falls back to the sites
  // lobby so the user lands somewhere useful (the step is blocked anyway).
  const firstSiteSlug = sites.find((s) => s.status === 'active')?.slug
    ?? sites[0]?.slug
    ?? null;

  const steps: SetupStep[] = STEP_DEFINITIONS.map((def) => {
    const complete = completionByStep[def.id];
    let blocked = false;
    if (def.id === 'create_first_site') {
      blocked = !requiredEntryStepsComplete;
    } else if (def.id === 'first_brainstorm') {
      blocked = !completionByStep.create_first_site;
    }
    const nextKey = def.requiredKeys.find((k) => !isKeyConfigured(entries, k));
    let href = def.href;
    if (def.id === 'first_brainstorm') {
      href = firstSiteSlug ? `/sites/${firstSiteSlug}/brainstorm` : '/sites';
    }
    return {
      id: def.id,
      title: def.title,
      description: def.description,
      complete,
      blocked,
      optional: def.optional,
      requiredKeys: def.requiredKeys,
      href,
      nextKey,
    };
  });

  const allRequiredComplete = steps
    .filter((s) => !s.optional)
    .every((s) => s.complete);

  let firstUnconfiguredKey: ManagedKeyName | null = null;
  for (const step of steps) {
    if (step.optional || step.complete) continue;
    if (step.nextKey) {
      firstUnconfiguredKey = step.nextKey;
      break;
    }
  }

  return { steps, allRequiredComplete, firstUnconfiguredKey };
}

export function useSetupStatus(): SetupStatus {
  const [snap, setSnap] = useState<SetupSnapshot | null>(cached);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(cached === null);

  useEffect(() => {
    let cancelled = false;
    const cb = (next: SetupSnapshot | null, err: string | null) => {
      if (cancelled) return;
      setSnap(next);
      setError(err);
      setLoading(false);
    };
    subscribers.add(cb);
    if (cached === null) {
      void loadSnapshot(false);
    } else {
      setLoading(false);
    }
    return () => {
      cancelled = true;
      subscribers.delete(cb);
    };
  }, []);

  const { steps, allRequiredComplete, firstUnconfiguredKey } = deriveSteps(snap);

  const refresh = async (): Promise<void> => {
    await loadSnapshot(true);
  };

  return {
    steps,
    entries: snap?.entries ?? [],
    allRequiredComplete,
    firstUnconfiguredKey,
    loading,
    error,
    refresh,
  };
}
